import {Component, Inject, OnDestroy, OnInit, Optional, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {map} from 'rxjs/operators';
import {TypesFormComponent} from '../../admin/medication-types/types-form/types-form.component';
import {MatDialog} from '@angular/material/dialog';
import {AppMatAutocompleteComponent} from '@xmedical/ui';
import {DiagnosisCode, MasterDataRepositoryService, Medication, MedicationRepositoryService, MedicationType} from '@xmedical/core';

@Component({
    selector: 'app-medication-item-add-card',
    templateUrl: './medication-item-add-card.component.html',
    styleUrls: ['./medication-item-add-card.component.scss'],
    standalone: false
})
export class MedicationItemAddCardComponent implements OnInit, OnDestroy {

  validate = false;
  form: UntypedFormGroup;
  medicationType: MedicationType = null;
  diagnosisCode: DiagnosisCode = null;
  @ViewChild('typeAutocomplete') autocomplete: AppMatAutocompleteComponent;
  @ViewChild('diagnosisAutocomplete') diagnosisAutocomplete: AppMatAutocompleteComponent;
  private repositorySubscription: Subscription;

  constructor(
    private dialogRef: MatDialogRef<MedicationItemAddCardComponent>,
    private dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private medicationRepository: MedicationRepositoryService,
    private masterDataRepository: MasterDataRepositoryService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { searchTerm?: string }) {
  }

  get hasType(): boolean {
    return this.form?.get('typeId').value !== null;
  }

  get hasDiagnosisCode(): boolean {
    return this.diagnosisCode !== null;
  }

  searchMedicationTypes$ = (query: string): Observable<MedicationType[]> => {
    return this.medicationRepository.queryTypes(query)
      .pipe(map(response => response));
  }

  searchDiagnosisCodes$ = (query: string): Observable<DiagnosisCode[]> => {
    return this.masterDataRepository.searchDiagnosisCodes(query);
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      description: [this.data?.searchTerm || '', Validators.required],
      dosage: ['', Validators.required],
      diagnosisCode: ['', Validators.required],
      typeId: [null],
      comment: [''],
      textColour: ['']
    });
  }

  ngOnDestroy(): void {
    if (this.repositorySubscription) {
      this.repositorySubscription.unsubscribe();
    }
  }

  save() {
    if (this.form.valid) {
      const medication = this.formToMedication();
      this.repositorySubscription = this.medicationRepository.createMedicationItem(medication).subscribe(id => {
        medication.id = id;  // Add the returned ID to the form data
        this.dialogRef.close(medication);
      });
    }
    this.validate = true;
  }

  close() {
    this.dialogRef.close();
  }

  typeSelected(medicationType: MedicationType) {
    this.medicationType = medicationType;
    this.form.get('typeId').patchValue(medicationType.id);
    this.form.updateValueAndValidity();
    this.autocomplete.cancelSearch();
  }

  addNewType() {
    const DialogRef = this.dialog.open(TypesFormComponent, {panelClass: 'mat-confirmation-dialog-responsive'});
  }

  clearType() {
    this.form.get('typeId').setValue(null);
    this.form.updateValueAndValidity();
  }

  diagnosisCodeSelected(code: DiagnosisCode) {
    this.diagnosisCode = code;
    this.form.get('diagnosisCode').patchValue(code.code);
    this.form.updateValueAndValidity();
    this.diagnosisAutocomplete?.cancelSearch();
  }

  clearDiagnosisCode() {
    this.diagnosisCode = null;
    this.form.get('diagnosisCode').setValue('');
    this.form.updateValueAndValidity();
  }

  private formToMedication(): Medication {
    return this.form.value as Medication;
  }
}
